import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Button,
    Input,
    Tabs,
    Space,
    Modal,
    message,
    Upload as AntUpload,
    Popconfirm,
    Tooltip,
    Typography,
    Flex,
    Select
} from 'antd';
import {
    DownloadOutlined,
    UploadOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    CopyOutlined,
    EditOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import superuserService from '@/services/superadminService';
import { useNavigate } from 'react-router-dom';
const { Option } = Select
const { TabPane } = Tabs;

// Formula engine for Excel-like calculations
class FormulaEngine {
    static evaluate(formula, data, currentRow, currentCol) {
        if (!formula.startsWith('=')) return formula;

        const expr = formula.substring(1);

        // Handle cell references (A1, B2, etc.)
        const cellRefRegex = /([A-Z]+)(\d+)/g;
        let processedExpr = expr.replace(cellRefRegex, (match, col, row) => {
            const colIndex = this.columnToIndex(col);
            const rowIndex = parseInt(row) - 1;

            if (data[rowIndex] && data[rowIndex][colIndex] !== undefined) {
                const cellValue = data[rowIndex][colIndex];
                return isNaN(cellValue) ? `"${cellValue}"` : cellValue;
            }
            return '0';
        });

        // Handle range references (A1:A5, etc.)
        const rangeRegex = /([A-Z]+)(\d+):([A-Z]+)(\d+)/g;
        processedExpr = processedExpr.replace(rangeRegex, (match, startCol, startRow, endCol, endRow) => {
            const startColIndex = this.columnToIndex(startCol);
            const endColIndex = this.columnToIndex(endCol);
            const startRowIndex = parseInt(startRow) - 1;
            const endRowIndex = parseInt(endRow) - 1;

            const values = [];
            for (let r = startRowIndex; r <= endRowIndex; r++) {
                for (let c = startColIndex; c <= endColIndex; c++) {
                    if (data[r] && data[r][c] !== undefined) {
                        const val = parseFloat(data[r][c]);
                        if (!isNaN(val)) values.push(val);
                    }
                }
            }
            return `[${values.join(',')}]`;
        });

        // Handle Excel functions
        processedExpr = this.handleExcelFunctions(processedExpr);

        try {
            // Create a safe evaluation context
            const func = new Function(`
        const SUM = (arr) => Array.isArray(arr) ? arr.reduce((a, b) => a + b, 0) : 0;
        const AVERAGE = (arr) => Array.isArray(arr) && arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
        const MAX = (arr) => Array.isArray(arr) && arr.length > 0 ? Math.max(...arr) : 0;
        const MIN = (arr) => Array.isArray(arr) && arr.length > 0 ? Math.min(...arr) : 0;
        const COUNT = (arr) => Array.isArray(arr) ? arr.length : 0;
        const IF = (condition, trueVal, falseVal) => condition ? trueVal : falseVal;
        const ABS = (num) => Math.abs(num);
        const ROUND = (num, digits = 0) => Math.round(num * Math.pow(10, digits)) / Math.pow(10, digits);
        const SQRT = (num) => Math.sqrt(num);
        const POWER = (base, exp) => Math.pow(base, exp);
        const PI = () => Math.PI;
        
        return ${processedExpr};
      `);

            return func();
        } catch (error) {
            return '#ERROR!';
        }
    }

    static handleExcelFunctions(expr) {
        return expr
            .replace(/SUM\(/g, 'SUM(')
            .replace(/AVERAGE\(/g, 'AVERAGE(')
            .replace(/MAX\(/g, 'MAX(')
            .replace(/MIN\(/g, 'MIN(')
            .replace(/COUNT\(/g, 'COUNT(')
            .replace(/IF\(/g, 'IF(')
            .replace(/ABS\(/g, 'ABS(')
            .replace(/ROUND\(/g, 'ROUND(')
            .replace(/SQRT\(/g, 'SQRT(')
            .replace(/POWER\(/g, 'POWER(')
            .replace(/PI\(\)/g, 'PI()');
    }

    static columnToIndex(col) {
        let result = 0;
        for (let i = 0; i < col.length; i++) {
            result = result * 26 + (col.charCodeAt(i) - 'A'.charCodeAt(0) + 1);
        }
        return result - 1;
    }

    static indexToColumn(index) {
        let result = '';
        while (index >= 0) {
            result = String.fromCharCode((index % 26) + 'A'.charCodeAt(0)) + result;
            index = Math.floor(index / 26) - 1;
        }
        return result;
    }
}

export default function ExcelSpreadsheet() {
    const [sheets, setSheets] = useState({
        'Sheet1': Array(20).fill().map(() => Array(10).fill(''))
    });
    const navigate  = useNavigate()
    const [activeSheet, setActiveSheet] = useState('Sheet1');
    const [selectedCell, setSelectedCell] = useState({ row: 0, col: 0 });
    const [formulaBar, setFormulaBar] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [workbookName, setWorkbookName] = useState('Workbook1');
    const [renameModalVisible, setRenameModalVisible] = useState(false);
    const [newSheetName, setNewSheetName] = useState('');
    const [sheetToRename, setSheetToRename] = useState('');
    const fileInputRef = useRef(null);
    const [worksheets, setWorksheets] = useState([])
    // Get current sheet data
    const data = sheets[activeSheet] || Array(20).fill().map(() => Array(10).fill(''));
    const sheetNames = Object.keys(sheets);

    const columns = Array.from({ length: data[0]?.length || 10 }, (_, i) =>
        FormulaEngine.indexToColumn(i)
    );

    // Update formula bar when cell selection changes
    useEffect(() => {
        const cellValue = data[selectedCell.row]?.[selectedCell.col] || '';
        setFormulaBar(cellValue);
    }, [selectedCell, data]);

    const updateCell = useCallback((row, col, value) => {
        setSheets(prevSheets => {
            const newSheets = { ...prevSheets };
            const currentSheetData = [...newSheets[activeSheet]];

            // Ensure the data array is large enough
            while (currentSheetData.length <= row) {
                currentSheetData.push(Array(currentSheetData[0]?.length || 10).fill(''));
            }
            while (currentSheetData[row].length <= col) {
                currentSheetData[row].push('');
            }

            currentSheetData[row][col] = value;

            // Recalculate all formulas in current sheet
            for (let r = 0; r < currentSheetData.length; r++) {
                for (let c = 0; c < currentSheetData[r].length; c++) {
                    if (typeof currentSheetData[r][c] === 'string' && currentSheetData[r][c].startsWith('=')) {
                        // Store original formula and calculate display value
                        const formula = currentSheetData[r][c];
                        currentSheetData[r][c] = {
                            formula,
                            value: FormulaEngine.evaluate(formula, currentSheetData, r, c)
                        };
                    }
                }
            }

            newSheets[activeSheet] = currentSheetData;
            return newSheets;
        });
    }, [activeSheet]);

    const getCellDisplayValue = (cellData) => {
        if (typeof cellData === 'object' && cellData.value !== undefined) {
            return cellData.value;
        }
        return cellData;
    };

    const getCellRawValue = (cellData) => {
        if (typeof cellData === 'object' && cellData.formula) {
            return cellData.formula;
        }
        return cellData;
    };

    const handleCellClick = (row, col) => {
        setSelectedCell({ row, col });
    };

    const handleCellChange = (row, col, value) => {
        updateCell(row, col, value);
    };

    const handleFormulaBarChange = (value) => {
        setFormulaBar(value);
        updateCell(selectedCell.row, selectedCell.col, value);
    };

    const addRow = () => {
        setSheets(prev => ({
            ...prev,
            [activeSheet]: [...prev[activeSheet], Array(prev[activeSheet][0]?.length || 10).fill('')]
        }));
    };

    const addColumn = () => {
        setSheets(prev => ({
            ...prev,
            [activeSheet]: prev[activeSheet].map(row => [...row, ''])
        }));
    };

    const removeRow = () => {
        if (data.length > 1) {
            setSheets(prev => ({
                ...prev,
                [activeSheet]: prev[activeSheet].slice(0, -1)
            }));
        }
    };

    const removeColumn = () => {
        if (data[0]?.length > 1) {
            setSheets(prev => ({
                ...prev,
                [activeSheet]: prev[activeSheet].map(row => row.slice(0, -1))
            }));
        }
    };

    // Sheet management functions
    const addSheet = () => {
        const newSheetNumber = sheetNames.length + 1;
        const newSheetName = `Sheet${newSheetNumber}`;
        setSheets(prev => ({
            ...prev,
            [newSheetName]: Array(20).fill().map(() => Array(10).fill(''))
        }));
        setActiveSheet(newSheetName);
    };

    const deleteSheet = (sheetName) => {
        if (sheetNames.length > 1) {
            const newSheets = { ...sheets };
            delete newSheets[sheetName];
            setSheets(newSheets);

            if (activeSheet === sheetName) {
                setActiveSheet(Object.keys(newSheets)[0]);
            }
            message.success(`Sheet "${sheetName}" deleted`);
        } else {
            message.error('Cannot delete the last sheet');
        }
    };

    const openRenameModal = (sheetName) => {
        setSheetToRename(sheetName);
        setNewSheetName(sheetName);
        setRenameModalVisible(true);
    };

    const handleRenameSheet = () => {
        if (newSheetName && newSheetName !== sheetToRename && !sheets[newSheetName]) {
            const newSheets = { ...sheets };
            newSheets[newSheetName] = newSheets[sheetToRename];
            delete newSheets[sheetToRename];
            setSheets(newSheets);

            if (activeSheet === sheetToRename) {
                setActiveSheet(newSheetName);
            }

            setRenameModalVisible(false);
            message.success(`Sheet renamed to "${newSheetName}"`);
        } else if (sheets[newSheetName]) {
            message.error('Sheet name already exists');
        }
    };

    const duplicateSheet = (sheetName) => {
        const originalData = sheets[sheetName];
        const newSheetName = `${sheetName}_Copy`;
        let counter = 1;
        let finalName = newSheetName;

        while (sheets[finalName]) {
            finalName = `${newSheetName}${counter}`;
            counter++;
        }

        setSheets(prev => ({
            ...prev,
            [finalName]: originalData.map(row => [...row])
        }));
        setActiveSheet(finalName);
        message.success(`Sheet duplicated as "${finalName}"`);
    };

    const exportToExcel = () => {
        const wb = XLSX.utils.book_new();

        Object.entries(sheets).forEach(([sheetName, sheetData]) => {
            const ws = XLSX.utils.aoa_to_sheet(sheetData.map(row =>
                row.map(cell => getCellRawValue(cell))
            ));
            XLSX.utils.book_append_sheet(wb, ws, sheetName);
        });

        XLSX.writeFile(wb, `${workbookName}.xlsx`);
        message.success('Workbook exported successfully');
    };

    const importFromExcel = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            const newSheets = {};

            workbook.SheetNames.forEach(sheetName => {
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

                const minRows = Math.max(20, jsonData.length);
                const minCols = Math.max(10, Math.max(...jsonData.map(row => row.length)));

                const paddedData = Array.from({ length: minRows }, (_, i) =>
                    Array.from({ length: minCols }, (_, j) => jsonData[i]?.[j] || '')
                );

                newSheets[sheetName] = paddedData;
            });

            setSheets(newSheets);
            setWorkbookName(file.name.replace(/\.[^/.]+$/, ""));
            setActiveSheet(workbook.SheetNames[0]);
            message.success('Workbook imported successfully');
        };
        reader.readAsArrayBuffer(file);
        return false; // Prevent upload
    };
console.log(
    "sheets",sheets
)
    const saveToDatabase = async () => {
        setIsLoading(true);

        try {
            for (const [sheetName, sheetData] of Object.entries(sheets)) {
                const headers = sheetData[0].filter(cell => getCellDisplayValue(cell) !== '');
                if (headers.length === 0) continue;

                const rows = sheetData.slice(1)
                    .filter(row => row.some(cell => getCellDisplayValue(cell) !== ''))
                    .map(row => headers.map((_, i) => getCellDisplayValue(row[i]) || null));

                const payload = {
                    tableName: `${sheetName}`,
                    headers: headers.map(header => ({
                        name: getCellDisplayValue(header).toLowerCase().replace(/[^a-z0-9]/g, '_'),
                        type: detectDataType(getCellDisplayValue(header), rows, headers.indexOf(header))
                    })),
                    data: rows
                };
                const apipayload = {
                    workbookName: workbookName,
                    sheets: payload
                };
                const res = superuserService.saveExcelSheet(apipayload);
                console.log('Saving to database:', res);

                if (res) {
                    message.success(`All sheets from "${workbookName}" saved to database`);
                }
             }

        } catch (error) {
            message.error('Error saving to database: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };
    console.log("worksheetsworksheets", worksheets)
    const detectDataType = (header, rows, columnIndex) => {
        const columnValues = rows.map(row => row[columnIndex]).filter(val => val !== null && val !== '');

        if (columnValues.length === 0) return 'TEXT';

        const allNumbers = columnValues.every(val => !isNaN(val) && !isNaN(parseFloat(val)));
        if (allNumbers) {
            const allIntegers = columnValues.every(val => Number.isInteger(parseFloat(val)));
            return allIntegers ? 'INTEGER' : 'DECIMAL(10,2)';
        }

        const allDates = columnValues.every(val => {
            const date = new Date(val);
            return date instanceof Date && !isNaN(date);
        });
        if (allDates) return 'TIMESTAMP';

        const allBooleans = columnValues.every(val =>
            ['true', 'false', '1', '0'].includes(String(val).toLowerCase())
        );
        if (allBooleans) return 'BOOLEAN';

        return 'TEXT';
    };

    const loadFromDatabase = async (tableName) => {
        setIsLoading(true);

        try {
            // Mock loading - replace with actual API call
            console.log('Loading from database:', tableName);
            message.success('Data loaded from database successfully');
        } catch (error) {
            message.error('Error loading from database: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const tabItems = sheetNames.map(sheetName => ({
        key: sheetName,
        label: (
            <div className="d-flex align-items-center">
                <span style={{ marginRight: '8px' }}>{sheetName}</span>
                <div className="btn-group" role="group">
                    <Tooltip title="Rename Sheet">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                openRenameModal(sheetName);
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Duplicate Sheet">
                        <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                duplicateSheet(sheetName);
                            }}
                        />
                    </Tooltip>
                    {sheetNames.length > 1 && (
                        <Popconfirm
                            title={`Delete sheet "${sheetName}"?`}
                            onConfirm={(e) => {
                                e?.stopPropagation();
                                deleteSheet(sheetName);
                            }}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </Popconfirm>
                    )}
                </div>
            </div>
        ),
    }));

    const getAllWorksheet = async () => {
        const getWorksheets = await superuserService.getAllWorksheet();
        setWorksheets(getWorksheets?.workbooks)
    }
    useEffect(() => {
        getAllWorksheet()
    }, [])
    const onChangeworkSheet = (e) => {
        console.log("eee", e)
    }
    return (
        <div className="vh-100 d-flex flex-column bg-light">
            {/* Toolbar */}
            <Flex justify='space-between'>
                <Typography.Title level={4} className='m-2'>Seaflash Excel</Typography.Title>
                <Space >
                    <Select style={{ width: "10vw" }} onChange={onChangeworkSheet}>
                    {worksheets?.length
                        ? worksheets.map((x, index) => (
                            <Option key={index} value={x.id || x.value}>
                                {x.name}
                            </Option>
                        ))
                        : null}
                </Select></Space>
            </Flex>
            <div className="bg-white border-bottom p-3">
                <div className="container-fluid">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <Input
                                value={workbookName}
                                onChange={(e) => setWorkbookName(e.target.value)}
                                placeholder="Workbook Name"
                                style={{ width: '200px' }}
                            />
                        </div>

                        <div className="col-auto">
                            <Space>
                                <AntUpload
                                    beforeUpload={importFromExcel}
                                    accept=".xlsx,.xls,.csv"
                                    showUploadList={false}
                                >
                                    <Button type="primary" icon={<UploadOutlined className='text-white' />}>
                                        Import
                                    </Button>
                                </AntUpload>

                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined className='text-white' />}
                                    onClick={exportToExcel}
                                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                >
                                    Export
                                </Button>

                                <Button
                                    type="primary"
                                    icon={<SaveOutlined className='text-white' />}
                                    onClick={saveToDatabase}
                                    loading={isLoading}
                                    style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                                >
                                    Save All to DB
                                </Button>

                                <Button
                                    type="primary"
                                    icon={<UploadOutlined className='text-white' />}
                                    onClick={() => {
                                        const tableName = prompt('Enter table name to load:');
                                        if (tableName) loadFromDatabase(tableName);
                                    }}
                                    loading={isLoading}
                                    style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                                >
                                    Load from DB
                                </Button>
                            </Space>
                        </div>

                        <div className="col-auto">
                            <Space>
                                <div className="btn-group" role="group">
                                    <Button icon={<PlusOutlined />} onClick={addRow} title="Add Row" />
                                    <Button icon={<DeleteOutlined />} onClick={removeRow} title="Remove Row" />
                                </div>
                                <span className="text-muted small">Rows</span>
                                <div className="btn-group" role="group">
                                    <Button icon={<PlusOutlined />} onClick={addColumn} title="Add Column" />
                                    <Button icon={<DeleteOutlined />} onClick={removeColumn} title="Remove Column" />
                                </div>
                                <span className="text-muted small">Cols</span>
                            </Space>
                        </div>

                        <div className="col-auto">
                            <Button
                                type="primary"
                                icon={<PlusOutlined className='text-white' />}
                                onClick={addSheet}
                                style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
                            >
                                Add Sheet
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sheet Tabs */}
            <div className="bg-light border-bottom">
                <Tabs
                    activeKey={activeSheet}
                    onChange={setActiveSheet}
                    type="editable-card"
                    onEdit={(targetKey, action) => {
                        if (action === 'add') {
                            addSheet();
                        }
                    }}
                    items={tabItems}
                    style={{ margin: 0 }}
                />
            </div>

            {/* Formula Bar */}
            <div className="bg-white border-bottom p-2">
                <div className="container-fluid">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <span className="badge bg-secondary">
                                {FormulaEngine.indexToColumn(selectedCell.col)}{selectedCell.row + 1}
                            </span>
                        </div>
                        <div className="col">
                            <Input
                                value={formulaBar}
                                onChange={(e) => setFormulaBar(e.target.value)}
                                onPressEnter={() => handleFormulaBarChange(formulaBar)}
                                placeholder="Enter formula or value (e.g., =SUM(A1:A5), =A1+B1)"
                                style={{ fontFamily: 'monospace' }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Spreadsheet */}
            <div className="flex-fill overflow-auto">
                <table className="table table-bordered table-sm mb-0">
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: '50px', minWidth: '50px' }}></th>
                            {columns.map((col, index) => (
                                <th key={col} style={{ minWidth: '80px', fontSize: '12px' }}>
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="table-light text-center" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                    {rowIndex + 1}
                                </td>
                                {row.map((cell, colIndex) => (
                                    <td
                                        key={`${rowIndex}-${colIndex}`}
                                        className={`p-0 ${selectedCell.row === rowIndex && selectedCell.col === colIndex
                                            ? 'table-primary'
                                            : ''
                                            }`}
                                        style={{
                                            minWidth: '80px',
                                            cursor: 'pointer',
                                            position: 'relative'
                                        }}
                                        onClick={() => handleCellClick(rowIndex, colIndex)}
                                    >
                                        <input
                                            type="text"
                                            value={getCellDisplayValue(cell)}
                                            onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                                            className="form-control border-0 rounded-0"
                                            style={{
                                                fontSize: '13px',
                                                backgroundColor: 'transparent',
                                                boxShadow: 'none'
                                            }}
                                            onFocus={() => {
                                                setSelectedCell({ row: rowIndex, col: colIndex });
                                                setFormulaBar(getCellRawValue(cell));
                                            }}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Status Bar */}
            <div className="bg-light border-top p-2">
                <div className="container-fluid">
                    <div className="row justify-content-between">
                        <div className="col-auto">
                            <small className="text-muted">Ready</small>
                        </div>
                        <div className="col-auto">
                            <small className="text-muted">
                                Active Sheet: {activeSheet} |
                                Cell: {FormulaEngine.indexToColumn(selectedCell.col)}{selectedCell.row + 1} |
                                Rows: {data.length} |
                                Cols: {data[0]?.length || 0} |
                                Total Sheets: {sheetNames.length}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rename Sheet Modal */}
            <Modal
                title="Rename Sheet"
                open={renameModalVisible}
                onOk={handleRenameSheet}
                onCancel={() => setRenameModalVisible(false)}
                okText="Rename"
                cancelText="Cancel"
            >
                <Input
                    value={newSheetName}
                    onChange={(e) => setNewSheetName(e.target.value)}
                    placeholder="Enter new sheet name"
                    onPressEnter={handleRenameSheet}
                />
            </Modal>
        </div>
    );
}